import { useMutation } from '@apollo/client/react'
import { Box, Button, HStack, Spinner } from '@chakra-ui/react'
import { UserAvatar } from '@src/components/sco/Account/UserAvatar'
import { stripMarkdownWhitespace } from '@src/components/sco/Chat/ChatMessageComposer'
import {
  CommentPendingAttachments,
  useCommentAttachments,
} from '@src/components/sco/Comments/CommentAttachments'
import { CommentCard } from '@src/components/sco/Comments/CommentCard'
import { RichMarkdownInput } from '@src/components/sco/RichMarkdownInput'
import { useBreakpointContext } from '@src/components/sco/utilities/BreakpointProvider'
import { ScoUser } from '@src/graphql-types'
import { useCurrentUser } from '@src/hooks/useCurrentUser'
import { gql } from '@src/lib/gql'
import React, { useState } from 'react'

type Props = {
  newCommentValues: {
    attachmentId?: string
    attachmentType?: string
    parentCommentId?: string
    parentCommentNodeId?: string
    threadId?: string
  }
  onSuccess?: (mutationResponse) => void
  placeholder?: string
  onError?: (error) => void
  onCancel?: () => void
}

export const CommentComposer = (props: Props) => {
  const { getBreakpointValue } = useBreakpointContext()
  const [body, setBody] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [submissionCount, setSubmissionCount] = useState(0)
  const { pendingFiles, addFile, removeFile, reset, isUploading, isFull, storedFileIds } =
    useCommentAttachments()
  const [createComment, createCommentMutation] = useMutation(CommentComposer.mutations.createComment())
  const { currentUser }: { currentUser?: ScoUser } = useCurrentUser()

  if (!currentUser) {
    return <Spinner />
  }

  const callCreateComment = async () => {
    const hasBody = stripMarkdownWhitespace(body).length > 0
    if ((!hasBody && storedFileIds.length === 0) || isUploading) {
      return
    }
    let mutationResponse = null
    await setIsLoading(true)
    try {
      mutationResponse = await createComment({
        variables: {
          input: {
            body: body || '',
            ...props.newCommentValues,
            storedFileIds,
          },
        },
      })
    } catch (err) {
      if (props.onError) {
        await props.onError(err)
      } else {
        alert('Comment could not be posted.')
      }
    }

    await setBody(null)
    reset()
    await setIsLoading(false)
    await setSubmissionCount(submissionCount + 1)

    if (props.onSuccess) {
      await props.onSuccess(mutationResponse)
    }
  }

  return (
    <HStack align={'top'} gap={'12px'} width={'full'}>
      <Box hidden={getBreakpointValue([true, true, false])}>
        <UserAvatar avatarProps={{ size: 'sm' }} disablePopover={true} user={currentUser} />
      </Box>
      <Box width={'full'}>
        <RichMarkdownInput
          key={submissionCount}
          enableFileAttachments={true}
          fileAttachmentsDisabled={isFull || isUploading || isLoading}
          placeholder={props.placeholder || 'Type your reply...'}
          additionalToolbarButtons={
            <>
              <Button
                disabled={isUploading}
                justifySelf={'flex-end'}
                loading={isLoading || isUploading || createCommentMutation.loading}
                marginLeft={'auto'}
                marginRight={'0.25em'}
                paddingX={'1.5em'}
                size={'sm'}
                variant={'cta'}
                onClick={() => callCreateComment()}
              >
                {isLoading || isUploading ? <Spinner size={'sm'} /> : 'Post'}
              </Button>
            </>
          }
          onChange={values => setBody(values.markdown)}
          onFileAttachmentSelected={addFile}
        />
        <CommentPendingAttachments disabled={isLoading} files={pendingFiles} onRemove={removeFile} />
      </Box>
    </HStack>
  )
}

CommentComposer.mutations = {
  createComment: () => gql`
    mutation($input: CreateCommentInput!) {
      createComment(input: $input) {
        comment {
          id
          commentId
          ${CommentCard.fragments.ScoComment()}
          parentComment {
            id
            commentId
            childCommentsCount
          }
        }
      }
    }
  `,
}

CommentComposer.fragments = {
  parentScoComment: () => gql`
    fragment _ on ScoComment {
      id
      commentId
      threadId
    }
  `,
}
